import { useCallback, useMemo } from 'react'
import { permissionActionsByName } from 'merklebase-utils/commons'
import useAuth from './useAuth'

const usePermissions = () => {
  const { user } = useAuth()

  const isRolesFeatureEnabled = useMemo(
    () => user?.featureFlags?.includes('roles') || false,
    [user]
  )

  // Users only feature is enabled if roles feature is disabled and users-only feature is enabled
  const isUsersOnlyFeatureEnabled = useMemo(
    () => (!isRolesFeatureEnabled && user?.featureFlags?.includes('users-only')) || false,
    [isRolesFeatureEnabled, user?.featureFlags]
  )

  const isOwner = useCallback(() => {
    if (!isRolesFeatureEnabled) {
      return true
    }

    return user?.isOwner
  }, [isRolesFeatureEnabled, user?.isOwner])

  const isAdmin = useCallback(() => {
    if (!isRolesFeatureEnabled) {
      return true
    }

    return user?.isAdmin
  }, [isRolesFeatureEnabled, user?.isAdmin])

  const isAdminOrOwner = useCallback(() => {
    if (!isRolesFeatureEnabled) {
      return true
    }

    return isAdmin() || isOwner()
  }, [isAdmin, isOwner, isRolesFeatureEnabled])

  const hasPermissionAccess = useCallback(
    (permissionId) => {
      if (!isRolesFeatureEnabled) {
        return true
      }

      if (isAdminOrOwner() || !permissionId) {
        return true
      }

      const hasViewOrEdit =
        user?.permissions?.[permissionId]?.[permissionActionsByName.View.id] ||
        user?.permissions?.[permissionId]?.[permissionActionsByName.Edit.id]

      return !!hasViewOrEdit
    },
    [isRolesFeatureEnabled, isAdminOrOwner, user?.permissions]
  )

  const hasPermissionAction = useCallback(
    (permissionId, actionId) => {
      if (!isRolesFeatureEnabled) {
        return true
      }

      if (isAdminOrOwner() || !permissionId || !actionId) {
        return true
      }

      return !!user?.permissions?.[permissionId]?.[actionId]
    },
    [isRolesFeatureEnabled, isAdminOrOwner, user?.permissions]
  )

  return {
    isRolesFeatureEnabled,
    isUsersOnlyFeatureEnabled,
    hasPermissionAccess,
    isAdminOrOwner,
    isAdmin,
    isOwner,
    hasPermissionAction,
  }
}

export default usePermissions
